import { LoadingOutlined } from '@ant-design/icons'
import { Checkbox, Flex, Row, Spin, Table } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import dashboardApi from '../../api/dashboard.api'
import { userState } from '../../store/user'
import '../../styles/home.scss'
import PeriodSearchFilter from '../shared/PeriodSearchFilter'

const RevenueDashboard = () => {
  const [user, setUser] = useRecoilState(userState)
  const [isLoading, setIsLoading] = useState(true)
  const [dateType, setDateType] = useState('today')
  const [searchOptions, setSearchOptions] = useState({
    startDate: dayjs().startOf('day'),
    endDate: dayjs().endOf('day'),
    isCooperate: false,
  })
  const [statistics, setStatistics] = useState({
    totalPrice: 0,
    numbers: 0,
    averageOrderValue: 0,
    productList: [],
  })

  /**
   * 통계 데이터 가져오기
   * @returns {Promise<void>}
   */
  const getRevenueStatistics = async () => {
    try {
      const param = {
        startDate: dayjs(searchOptions.startDate)
          .startOf('day')
          .format('YYYY-MM-DD HH:mm:ss'),
        endDate: dayjs(searchOptions.endDate)
          .endOf('day')
          .format('YYYY-MM-DD HH:mm:ss'),
        isCooperate: searchOptions.isCooperate,
      }
      const res = await dashboardApi.getRevenueStatistics(param, user.id)
      // console.log('res : ', res)
      if (res.data.code === 200) {
        setStatistics((prev) => ({
          ...prev,
          totalPrice: res.data.result.totalPurchaseAmount ?? 0,
          numbers: res.data.result.purchasesNum ?? 0,
          productList: res.data.result.productListWithOrder ?? [],
        }))
      }
    } catch (e) {
      console.log('getRevenueStatistics err : ', e)
    } finally {
      setIsLoading(false)
    }
  }
  useEffect(() => {
    if (searchOptions?.startDate && searchOptions?.endDate) {
      getRevenueStatistics()
    }
  }, [searchOptions])

  // 판매건수와 총 매출액에 대한 객단가 계산 & 무한 렌더링 방지
  useEffect(() => {
    if (
      statistics?.numbers !== undefined &&
      statistics?.totalPrice !== undefined
    ) {
      const averageOrderValue =
        statistics.numbers === 0
          ? 0
          : Math.floor(Number(statistics.totalPrice / statistics.numbers))

      if (statistics.averageOrderValue !== averageOrderValue) {
        setStatistics((prev) => ({
          ...prev,
          averageOrderValue: averageOrderValue.toLocaleString(),
        }))
      }
    }
  }, [statistics.numbers, statistics.totalPrice, statistics.averageOrderValue])

  const handleCooperateCheck = (e) => {
    const checked = e.target.checked
    if (checked) {
      setSearchOptions({
        ...searchOptions,
        isCooperate: true,
      })
    } else {
      setSearchOptions({
        ...searchOptions,
        isCooperate: false,
      })
    }
  }

  const listTitle = (key) => {
    const titles = {
      totalPrice: { text: '총 매출액', color: '#117ffa' },
      numbers: { text: '판매건수', color: '#1ca717' },
      averageOrderValue: { text: '객단가', color: '#bbc71d' },
      productList: { text: '상품별 상세 내역', color: '#191A1C' },
    }
    return titles[key] || { text: '', color: '#191A1C' }
  }

  const formatStatisticsValue = (key, value) => {
    if (key === 'numbers') {
      return `${value?.toLocaleString()} 건`
    }
    return `${value?.toLocaleString()} 원`
  }

  const columns = [
    {
      title: '상품명',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
      width: '35%',
      align: 'center',
    },
    {
      title: '옵션명',
      dataIndex: 'optionTitle',
      key: 'optionTitle',
      ellipsis: true,
      width: '35%',
      align: 'center',
    },
    {
      title: '매출금액',
      dataIndex: 'price1',
      key: 'price1',
      ellipsis: true,
      width: '15%',
      align: 'center',
      render: (value) => {
        return (
          <span
            style={{
              color: '#117ffa',
              maxWidth: '15%',
              overflow: 'hidden',
            }}
          >
            {value?.toLocaleString()}
          </span>
        )
      },
    },
    {
      title: '판매수량',
      dataIndex: 'totalCnt',
      key: 'totalCnt',
      width: '15%',
      align: 'center',
      render: (value) => {
        return (
          <span style={{ color: '#1ca717' }}>{value?.toLocaleString()}</span>
        )
      },
    },
  ]

  return (
    <Flex
      vertical
      gap={15}
      style={{ minWidth: '420px', height: '100%' }}
      className={`home-dashboard-component ${isLoading ? 'container-disable' : ''}`}
    >
      {isLoading && (
        <Spin
          className={'home-dashboard-loading'}
          indicator={<LoadingOutlined spin />}
          size={'large'}
        />
      )}
      <Flex
        align={'center'}
        justify={'space-between'}
        className={'home-dashboard-title'}
      >
        <h2>매출/판매 통계</h2>
        <Checkbox onChange={handleCooperateCheck}>공동 구매만 보기</Checkbox>
      </Flex>
      <PeriodSearchFilter
        dateType={dateType}
        setDateType={setDateType}
        searchOptions={searchOptions}
        setSearchOptions={setSearchOptions}
      />
      <div className={`revenue-start-end-date-title`}>
        {dayjs(searchOptions.startDate).format('YYYY.MM.DD')} ~{' '}
        {dayjs(searchOptions.endDate).format('YYYY.MM.DD')}
      </div>
      {/* 총 매출액, 판매건수 객단가 */}
      <Flex justify={'space-between'} align={'center'} gap={10}>
        {Object.keys(statistics).map((key, idx) => {
          const { text, color } = listTitle(key)
          return (
            key !== 'productList' && (
              <Flex
                vertical
                justify={'center'}
                align={'center'}
                gap={5}
                key={`user_list_${idx}`}
                className={'user-statistics-div'}
              >
                <p className={'user-statistics-title'}>{text}</p>
                <Flex align={'center'} className={'user-statistics-info-wrap'}>
                  <span style={{ color }} className={'user-statistics-info'}>
                    {formatStatisticsValue(key, statistics[key])}
                  </span>
                </Flex>
              </Flex>
            )
          )
        })}
      </Flex>
      {/* 상품별 상세내역 */}
      <Row
        className={`order-component-each`}
        align={'middle'}
        // style={{ marginBottom: '14px' }}
      >
        <div className={`order-component-each-detail`}>
          <h4
            className={`order-each-detail-title`}
            style={{ marginBottom: '12px' }}
          >
            상품별 상세내역
          </h4>
          <Table
            style={{ height: '191px', marginBlock: '4px' }}
            columns={columns}
            dataSource={statistics.productList}
            loading={isLoading}
            bordered
            size="middle"
            rowKey="id"
            rowClassName="coupon-table-row"
            pagination={false}
            scroll={{ y: 150 }}
          />
        </div>
      </Row>
    </Flex>
  )
}

export default RevenueDashboard
